import { useState, useCallback, useMemo } from 'react';

const STORAGE_PREFIX = 'ovo-table-config-';

function loadConfig(tableKey) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConfig(tableKey, config) {
  try {
    localStorage.setItem(STORAGE_PREFIX + tableKey, JSON.stringify(config));
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * Hook for configurable table columns with localStorage persistence.
 *
 * @param {string} tableKey - Unique key for localStorage (e.g., 'routes-columns')
 * @param {Array} allColumns - Full column definitions array from the table component
 * @param {Array} fixedKeys - Column keys that cannot be hidden or reordered (e.g., ['id', 'action'])
 * @returns {{ visibleColumns: Array, drawerOpen: boolean, openDrawer: Function, closeDrawer: Function, hidden: Set, order: Array, toggleColumn: Function, moveColumn: Function, resetConfig: Function }}
 */
const DEFAULT_PAGE_SIZE = 15;

export default function useColumnConfig(tableKey, allColumns, fixedKeys = []) {
  const defaultOrder = allColumns.map((col) => col.key);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [config, setConfig] = useState(() => {
    const saved = loadConfig(tableKey);
    return {
      hidden: new Set(saved?.hidden || []),
      order: saved?.order || defaultOrder,
      pageSize: saved?.pageSize || DEFAULT_PAGE_SIZE,
    };
  });

  const persist = useCallback(
    (newConfig) => {
      saveConfig(tableKey, {
        hidden: Array.from(newConfig.hidden),
        order: newConfig.order,
        pageSize: newConfig.pageSize,
      });
    },
    [tableKey],
  );

  const toggleColumn = useCallback(
    (key) => {
      setConfig((prev) => {
        const hidden = new Set(prev.hidden);
        if (hidden.has(key)) {
          hidden.delete(key);
        } else {
          hidden.add(key);
        }
        const next = { ...prev, hidden };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const moveColumn = useCallback(
    (fromIndex, toIndex) => {
      setConfig((prev) => {
        // Build clean order matching what the drawer actually displays (orderedAllColumns)
        const colMap = new Map(allColumns.map((col) => [col.key, col]));
        const cleanOrder = [];
        for (const key of prev.order) {
          if (colMap.has(key)) {
            cleanOrder.push(key);
            colMap.delete(key);
          }
        }
        for (const [key] of colMap) {
          cleanOrder.push(key);
        }
        // Now splice on the clean order so indices match the visual list
        const [moved] = cleanOrder.splice(fromIndex, 1);
        cleanOrder.splice(toIndex, 0, moved);
        const next = { ...prev, order: cleanOrder };
        persist(next);
        return next;
      });
    },
    [persist, allColumns],
  );

  const setPageSize = useCallback(
    (size) => {
      setConfig((prev) => {
        const next = { ...prev, pageSize: size };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const resetConfig = useCallback(() => {
    const next = { hidden: new Set(), order: defaultOrder, pageSize: DEFAULT_PAGE_SIZE };
    setConfig(next);
    persist(next);
  }, [defaultOrder, persist]);

  const visibleColumns = useMemo(() => {
    const colMap = new Map(allColumns.map((col) => [col.key, col]));
    const ordered = [];

    // Add columns in saved order
    for (const key of config.order) {
      if (colMap.has(key) && !config.hidden.has(key)) {
        ordered.push(colMap.get(key));
        colMap.delete(key);
      } else {
        colMap.delete(key);
      }
    }

    // Add any new columns not in saved order (appended at end, visible by default)
    for (const [key, col] of colMap) {
      if (!config.hidden.has(key)) {
        ordered.push(col);
      }
    }

    return ordered;
  }, [allColumns, config]);

  // Ordered list of all column keys (for the drawer) — includes hidden ones
  const orderedAllColumns = useMemo(() => {
    const colMap = new Map(allColumns.map((col) => [col.key, col]));
    const result = [];

    for (const key of config.order) {
      if (colMap.has(key)) {
        result.push(colMap.get(key));
        colMap.delete(key);
      }
    }
    for (const [, col] of colMap) {
      result.push(col);
    }

    return result;
  }, [allColumns, config.order]);

  return {
    visibleColumns,
    drawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
    hidden: config.hidden,
    order: config.order,
    pageSize: config.pageSize,
    orderedAllColumns,
    fixedKeys,
    toggleColumn,
    moveColumn,
    setPageSize,
    resetConfig,
  };
}
